






















































































































































































































































































































































































import Vue from "vue";
import eventBus from "@/services/eventBus";
import ConfirmationBox from "@/components/ConfirmationBox.vue";
import DateInput from "@/components/DateInput.vue";
import PreAccountingApi from "../services/PreAccountingApi";
import GeneralIngoingInvoiceApi from "@/modules/generalIngoingInvoice/services/GeneralIngoingInvoiceApi";
import {
  PreAccInvoiceDoc,
  PreAccProjectChoice,
  PreAccProjectHeadType,
  PreAccProjectInvItem,
  PreAccProjectLink,
  PreAccProjectOverview,
  PreAccProjectOverviewDefaultProps,
} from "../models/PreAccEditing";
import { t } from "@/plugins/i18n";

interface Data {
  overview: PreAccProjectOverview;
  projects: PreAccProjectChoice[];
  // K2D dw_geni_doc: a számla csatolt dokumentumai; a jobb felső panel füle (tab_item)
  documents: PreAccInvoiceDoc[];
  rightPanelIndex: number; // 0 = tételek, 1 = dokumentumok
  // K2D dw_project ue_filter szűrői
  projectFilterText: string;
  onlyOpenProjects: boolean;
  onlyWorkNumberProjects: boolean;
  selectedLink: PreAccProjectLink | null;
  selectedItem: PreAccProjectInvItem | null;
  selectedProject: PreAccProjectChoice | null;
  amountValue: number | null;
  headTypeValue: number;
  isSaving: boolean;
  loading: boolean;
  // a sárga kérdés-sáv és a megerősítéskor futó művelet
  questionVisible: boolean;
  questionMessage: string;
  pendingConfirm: (() => void) | null;
  pendingReject: (() => void) | null;
  reAccountConfirmVisible: boolean;
  // a kijelölt kapcsolat szerkesztett mezői (K2D dw_project_item)
  editHeadType: number;
  editAmount: number | null;
  editRemark: string;
  // K2D ib_stock_warned / ib_in_vat_warned: a fejhez kötés kérdései ablakonként egyszer
  stockWarned: boolean;
  vatWarned: boolean;
  // K2D pb_ok "Nem módosított az adatokon!": a kiosztás-kérdés csak akkor jön kilépéskor,
  // ha ezen a képernyőn történt mentett módosítás (felvitel, törlés, módosítás)
  sessionModified: boolean;
}

interface Methods {
  load(): Promise<void>;
  loadProjects(): Promise<void>;
  loadDocuments(): Promise<void>;
  documentUrl(doc: PreAccInvoiceDoc): string;
  reload(): void;
  formatMoney(value: number): string;
  roundMoney(value: number): number;
  onLinkClick(e: any): void;
  onItemClick(e: any): void;
  onProjectClick(e: any): void;
  askQuestion(message: string, onAccept: () => void, onReject?: () => void): void;
  acceptQuestion(): void;
  rejectQuestion(): void;
  onBeforeUnload(e: BeforeUnloadEvent): void;
  assignHead(): void;
  assign(itemId: number): Promise<void>;
  deleteLink(): Promise<void>;
  saveLink(): Promise<void>;
  resetLinkEdit(): void;
  reAccount(): Promise<void>;
  refreshAmountDefault(): void;
  refreshHeadTypeDefault(): void;
  goBack(): void;
}

interface Computed {
  invNo: number;
  canEdit: boolean;
  hasItems: boolean;
  showItems: boolean;
  documentColumns: any[];
  availableHeadTypes: PreAccProjectHeadType[];
  editHeadTypes: PreAccProjectHeadType[];
  linkDirty: boolean;
  headLinkSum: number;
  allocationText: string;
  overAllocationMessage: string;
  partialAllocationMessage: string;
  leaveWarning: string;
  linkRows: any[];
  itemRows: any[];
  filteredProjects: PreAccProjectChoice[];
  projectRows: any[];
  linkColumns: any[];
  itemColumns: any[];
  projectColumns: any[];
  remainingAmount: number;
}

export default Vue.extend<Data, Methods, Computed, {}>({
  components: { ConfirmationBox, DateInput },

  data() {
    return {
      overview: { ...PreAccProjectOverviewDefaultProps },
      projects: [] as PreAccProjectChoice[],
      documents: [] as PreAccInvoiceDoc[],
      rightPanelIndex: 0,
      projectFilterText: "",
      onlyOpenProjects: true,
      // K2D cbx_worknumber: checked = true (csak PO-kezelésnél látszik és hat)
      onlyWorkNumberProjects: true,
      selectedLink: null,
      selectedItem: null,
      selectedProject: null,
      amountValue: null,
      headTypeValue: 0,
      isSaving: false,
      loading: false,
      questionVisible: false,
      questionMessage: "",
      pendingConfirm: null,
      pendingReject: null,
      reAccountConfirmVisible: false,
      editHeadType: 0,
      editAmount: null,
      editRemark: "",
      stockWarned: false,
      vatWarned: false,
      sessionModified: false,
    };
  },

  // K2D pb_ok: a "nem a teljes összeg van kiosztva — folytatja?" kérdés a TF-ben a képernyő
  // elhagyásakor jön (a TF minden műveletet azonnal ment, nincs külön OK), és ide tartozik a
  // mentetlen kapcsolat-módosítás elvesztésének kérdése is. A navigáció a válaszig függőben
  // marad: Megerősítés = next(), Mégse = next(false) — így a böngésző Vissza gombja és a
  // replace-navigáció is a saját módján megy tovább
  beforeRouteLeave(to: any, from: any, next: any) {
    if (!this.leaveWarning) {
      next();
      return;
    }
    this.askQuestion(
      this.leaveWarning,
      () => next(),
      () => next(false)
    );
  },

  async mounted() {
    // böngészőfül bezárása / frissítése mentetlen kapcsolat-módosítással (BankPreAccEditorView minta)
    window.addEventListener("beforeunload", this.onBeforeUnload);
    this.loading = true;
    try {
      await this.load();
      await this.loadProjects();
      await this.loadDocuments();
    } finally {
      this.loading = false;
    }
  },

  beforeDestroy() {
    window.removeEventListener("beforeunload", this.onBeforeUnload);
  },

  computed: {
    invNo() {
      return Number(this.$route.params.invNo) || 0;
    },

    // K2D pfc_postopen (jóváhagyási állapot) + pb_ok (acc_status 6)
    canEdit() {
      return this.overview.editable && this.overview.accStatus !== 6;
    },

    hasItems() {
      return this.overview.items.length > 0;
    },

    showItems() {
      return this.hasItems && this.rightPanelIndex === 0;
    },

    // K2D d_g_inigeninv_pict_os: fájl neve, leírás (+ a dokumentum típusa)
    documentColumns: () => [
      { field: "fileName", title: t("FajlNeve"), cell: "fileNameCell" },
      { field: "description", title: t("Leiras") },
      { field: "docTypeName", title: t("Tipus"), width: "130px" },
    ],

    // a számla-szintű (fej) kapcsolatok összege
    headLinkSum() {
      return this.roundMoney(
        this.overview.links.filter((l) => l.itemId === 0).reduce((sum, l) => sum + (Number(l.amount) || 0), 0)
      );
    },

    // a kiosztás állása a kapcsolatok fejlécében
    allocationText() {
      const links = this.overview.links;
      if (links.length === 0) return "";
      const currency = this.overview.currency;
      if (links.some((l) => l.itemId === 0)) {
        return `Fejre kiosztva: ${this.formatMoney(this.headLinkSum)} / ${this.formatMoney(this.overview.currNettoWDpaym)} ${currency}`;
      }
      const total = this.roundMoney(links.reduce((sum, l) => sum + (Number(l.amount) || 0), 0));
      return t("PreAccounting_TetelekreKiosztvaTetelenkentATetelListaban", [this.formatMoney(total), currency]);
    },

    // K2D pb_ok hiba-ágai (928 / 949): túlkiosztás — felvitelkor a backend nem engedi, de
    // utólag keletkezhet (csökkent a nettó vagy a berendezhető összeg)
    overAllocationMessage() {
      const links = this.overview.links;
      if (links.length === 0) return "";
      if (links.some((l) => l.itemId === 0)) {
        return Math.abs(this.headLinkSum) > Math.abs(this.overview.currNettoWDpaym)
          ? t("PreAccounting_ABejovoSzamlaNettoOsszegeKisebb")
          : "";
      }
      const overItem = this.overview.items.some((item) => {
        const sum = this.roundMoney(
          links.filter((l) => l.itemId === item.id).reduce((acc, l) => acc + (Number(l.amount) || 0), 0)
        );
        return Math.abs(sum) > Math.abs(item.projAmount);
      });
      return overItem
        ? t("PreAccounting_VanOlyanBejovoSzamlaTetelMelynek")
        : "";
    },

    // K2D pb_ok összeg-ellenőrzése (előjeltől függetlenül, mint a backend): fej-kapcsolatnál a
    // számla nettója (+ előleg), tétel-kapcsolatnál minden érintett tétel berendezhető összege
    partialAllocationMessage() {
      const links = this.overview.links;
      if (links.length === 0) return "";
      if (links.some((l) => l.itemId === 0)) {
        return Math.abs(this.headLinkSum) < Math.abs(this.overview.currNettoWDpaym)
          ? t("PreAccounting_ABejovoSzamlaNettoOsszegeNem")
          : "";
      }
      const partialItem = this.overview.items.some((item) => {
        const sum = this.roundMoney(
          links.filter((l) => l.itemId === item.id).reduce((acc, l) => acc + (Number(l.amount) || 0), 0)
        );
        return sum !== 0 && Math.abs(sum) < Math.abs(item.projAmount);
      });
      return partialItem
        ? t("PreAccounting_VanOlyanBejovoSzamlaTetelMelynek_2")
        : "";
    },

    // A kilépési kérdés. A kiosztás-ellenőrzés csak akkor fut, ha ezen a képernyőn történt
    // mentett módosítás, és a kapcsolat szerkeszthető (K2D pb_ok: "Nem módosított az
    // adatokon!" → kérdés nélkül kilép; csak olvasható ablakban az OK tiltott)
    leaveWarning() {
      const parts: string[] = [];
      if (this.linkDirty) parts.push(t("PreAccounting_AKijeloltKapcsolatModositasaNincsElmentve"));
      if (this.sessionModified && this.canEdit) {
        if (this.overAllocationMessage) parts.push(this.overAllocationMessage);
        else if (this.partialAllocationMessage) parts.push(this.partialAllocationMessage);
      }
      // K2D pb_ok 788-797: az újrakontírozásról a mentéskor kötelező dönteni
      if (this.reAccountConfirmVisible) parts.push(t("PreAccounting_AzUjrakontirozasMegNemTortentMeg"));
      return parts.length > 0 ? `${parts.join(" ")} Biztosan folytatja?` : "";
    },

    // K2D ue_ht_filter: tétel kijelölésével a tétel-csoport, különben a fej-csoport típusai
    availableHeadTypes() {
      const isItem = !!this.selectedItem;
      return this.overview.headTypes.filter((x) => x.isItem === isItem);
    },

    // K2D itemchanged 'head_type': a kijelölt kapcsolat csoportjának (fej / tétel) típusai;
    // a már nem választható régi típus is a listában marad, hogy megmaradhasson
    editHeadTypes() {
      const link = this.selectedLink;
      if (!link) return [];
      const isItem = link.itemId > 0;
      const list = this.overview.headTypes.filter((x) => x.isItem === isItem);
      if (list.some((x) => x.headType === link.headType)) return list;
      return [{ headType: link.headType, name: link.headTypeName, isItem, isDefault: false }, ...list];
    },

    linkDirty() {
      const link = this.selectedLink;
      if (!link) return false;
      return (
        this.editHeadType !== link.headType ||
        this.editAmount !== link.amount ||
        (this.editRemark ?? "").trim() !== (link.remark ?? "").trim()
      );
    },

    linkRows() {
      return this.overview.links.map((x) => ({
        ...x,
        // K2D: számla-szintű kapcsolatnál a tétel-oszlop üres
        itemName: x.itemName || (x.itemId > 0 ? "" : t("PreAccounting_SzamlaFej")),
        selected: this.selectedLink?.id === x.id,
      }));
    },

    itemRows() {
      return this.overview.items.map((x) => ({
        ...x,
        // a tételhez rendelt kapcsolatok összege
        allocated: this.roundMoney(
          this.overview.links.filter((l) => l.itemId === x.id).reduce((sum, l) => sum + (Number(l.amount) || 0), 0)
        ),
        selected: this.selectedItem?.id === x.id,
      }));
    },

    // K2D dw_project ue_filter: "Csak munkaszámos" (po_worknumber <> ""), "Még nem lezárt"
    // (status <> 5), a gyorskereső pedig a munkaszámra (PO-kezelésnél), a névre, a belső és
    // külső ID-ra (tartalmazás, kis-nagybetű függetlenül), szám esetén a projekt ID-ra (egyezés)
    filteredProjects() {
      const isPo = this.overview.isPo;
      const text = (this.projectFilterText ?? "").trim().toLocaleUpperCase("hu");
      const id = /^\d+$/.test(text) ? Number(text) : 0;
      const contains = (value: string) => (value ?? "").toLocaleUpperCase("hu").includes(text);
      return this.projects.filter((x) => {
        if (isPo && this.onlyWorkNumberProjects && !x.poWorkNumber) return false;
        if (this.onlyOpenProjects && x.status === 5) return false;
        if (!text) return true;
        return (
          (isPo && contains(x.poWorkNumber)) ||
          contains(x.name) ||
          contains(x.mainCode) ||
          contains(x.code) ||
          (id > 0 && x.id === id)
        );
      });
    },

    projectRows() {
      return this.filteredProjects.map((x) => ({
        ...x,
        selected: this.selectedProject?.id === x.id,
      }));
    },

    // K2D ue_insert előtöltése: a még ki nem osztott maradék a kijelölt célra (fej: a
    // curr_netto + előlegek; tétel: a berendezhető összeg), ha a kiosztott kevesebb
    remainingAmount() {
      const isItem = !!this.selectedItem;
      const reference = isItem ? this.selectedItem!.projAmount : this.overview.currNettoWDpaym;
      const allocated = this.overview.links
        .filter((l) => (isItem ? l.itemId === this.selectedItem!.id : l.itemId === 0))
        .reduce((sum, l) => sum + (Number(l.amount) || 0), 0);
      return Math.abs(reference) > Math.abs(allocated) ? Math.round((reference - allocated) * 100) / 100 : 0;
    },

    // K2D dw_project_item oszlopai: tétel neve, összeg, pénznem, munkaszám, projekt
    linkColumns: () => [
      { field: "itemName", title: t("Tetel") },
      { field: "projectName", title: t("Projekt") },
      { field: "headTypeName", title: t("PreAccounting_KapcsolatTipusa"), width: "140px" },
      { field: "workNumber", title: t("Munkaszam"), width: "110px" },
      { field: "amount", title: t("Osszeg"), cell: "amountCell", width: "110px" },
      { field: "currency", title: t("Penznem"), width: "80px" },
      { field: "remark", title: t("Megjegyzes") },
    ],

    // K2D dw_geni_item: név, mennyiség, devizás nettó, berendezhető összeg (sfngeniitpramount)
    // + a már kiosztott összeg
    itemColumns: () => [
      { field: "name", title: t("TetelNeve") },
      { field: "quantity", title: t("PreAccounting_Menny"), cell: "quantityCell", width: "70px" },
      { field: "currNetto", title: t("Interface_NettoDev"), cell: "nettoCell", width: "105px" },
      { field: "projAmount", title: t("PreAccounting_Berendezheto"), cell: "projAmountCell", width: "105px" },
      { field: "allocated", title: t("PreAccounting_Kiosztva"), cell: "allocatedCell", width: "105px" },
    ],

    // K2D dw_project (d_g_project_os) oszlop-sorrendje: [munkaszám — csak PO-kezelésnél],
    // Külső ID (code), Belső ID (main_code), Név, Állapot, ID
    projectColumns() {
      return [
        ...(this.overview.isPo ? [{ field: "poWorkNumber", title: t("Munkaszam"), width: "110px" }] : []),
        { field: "code", title: t("KulsoID"), width: "100px" },
        { field: "mainCode", title: t("BelsoID"), width: "100px" },
        { field: "name", title: t("Nev") },
        { field: "statusName", title: t("Allapot"), width: "140px" },
        { field: "id", title: "ID", width: "70px" },
      ];
    },
  },

  watch: {
    // K2D ue_filter: szűrés után a kijelölés megmarad, ha a projekt még látszik. Ha kiesett,
    // a kijelölés megszűnik (a K2D az első sorra ugrana, de a TF a gombnyomásra azonnal ment,
    // ezért nem jelölünk ki magától projektet)
    filteredProjects(list: PreAccProjectChoice[]) {
      if (this.selectedProject && !list.some((x) => x.id === this.selectedProject!.id)) {
        this.selectedProject = null;
      }
    },
  },

  methods: {
    async load() {
      const selectedLinkId = this.selectedLink?.id ?? 0;
      try {
        this.overview = await PreAccountingApi.getGeniProjectOverview(this.invNo);
      } catch {
        eventBus.$emit("error", t("PreAccounting_AProjektKapcsolatokNemToltodtekBe"));
      }
      // a kijelölt kapcsolat és tétel újratöltés után is kijelölve marad (módosítás után látszik
      // az eredmény) — a friss sorra cseréljük, hogy a régi objektum értékei ne ragadjanak be
      this.selectedLink = this.overview.links.find((x) => x.id === selectedLinkId) ?? null;
      const selectedItemId = this.selectedItem?.id ?? 0;
      this.selectedItem = this.overview.items.find((x) => x.id === selectedItemId) ?? null;
      this.resetLinkEdit();
      this.refreshAmountDefault();
      this.refreshHeadTypeDefault();
    },

    // K2D dw_geni_doc: a csatolt dokumentumok — a kontír-szerkesztő adatlapjának listája
    // (ugyanaz az iminigeninv_pict); hiba esetén üres lista, a képernyő ettől még használható
    async loadDocuments() {
      try {
        const info = await PreAccountingApi.getInvoiceInfo("GENI", this.invNo);
        this.documents = info.documents ?? [];
      } catch {
        this.documents = [];
      }
    },

    // K2D ue_doksik: a bejövő számla modul meglévő fájl-végpontja (új fülön nyílik)
    documentUrl(doc: PreAccInvoiceDoc): string {
      return GeneralIngoingInvoiceApi.generateFileDownLoadLink(doc.id);
    },

    // K2D pb_cancel: újraolvasás — a mentetlen kapcsolat-módosítás elvetése előtt rákérdez
    reload() {
      const run = async () => {
        this.loading = true;
        try {
          await this.load();
          await this.loadProjects();
          await this.loadDocuments();
          const selectedProjectId = this.selectedProject?.id ?? 0;
          this.selectedProject = this.projects.find((x) => x.id === selectedProjectId) ?? null;
        } finally {
          this.loading = false;
        }
      };
      if (this.linkDirty) {
        this.askQuestion(t("PreAccounting_AKijeloltKapcsolatModositasaNincsElmentve_2"), run);
        return;
      }
      run();
    },

    // K2D dw_project (d_g_project_os): minden nem meghiúsult projekt — partnerre a K2D sem szűr
    async loadProjects() {
      try {
        this.projects = await PreAccountingApi.getGeniProjectChoices();
      } catch {
        this.projects = [];
        return;
      }
      // K2D pfc_postopen: üres lista esetén figyelmeztet (és bezárja az ablakot)
      if (this.projects.length === 0) {
        eventBus.$emit("warn", t("PreAccounting_NincsOlyanProjektAmihezABejovo"));
      }
    },

    formatMoney(value: number): string {
      return new Intl.NumberFormat("hu-HU", { maximumFractionDigits: 2 }).format(value ?? 0);
    },

    // összegzés után a lebegőpontos maradék levágása (a pénzösszegek 2 tizedesek)
    roundMoney(value: number): number {
      return Math.round(value * 100) / 100;
    },

    // K2D ue_insert: az összeg-mező a maradékkal indul (0-nál üres, mert 0 nem adható)
    refreshAmountDefault() {
      this.amountValue = this.remainingAmount !== 0 ? this.remainingAmount : null;
    },

    // K2D ue_insert: a csoport alapértelmezett típusa; ha nincs, a felhasználó választ
    refreshHeadTypeDefault() {
      const list = this.availableHeadTypes;
      if (list.some((x) => x.headType === this.headTypeValue)) return;
      const def = list.find((x) => x.isDefault);
      this.headTypeValue = def ? def.headType : 0;
    },

    onLinkClick(e: any) {
      const select = () => {
        this.selectedLink = this.overview.links.find((x) => x.id === e.dataItem.id) ?? null;
        this.resetLinkEdit();
      };
      // másik kapcsolatra váltáskor a mentetlen módosítás nem veszhet el szó nélkül
      if (this.linkDirty && this.selectedLink?.id !== e.dataItem.id) {
        this.askQuestion(t("PreAccounting_AKijeloltKapcsolatModositasaNincsElmentve_3"), select);
        return;
      }
      select();
    },

    onItemClick(e: any) {
      // ismételt kattintás = a kijelölés levétele (a számla-szintű gombhoz)
      this.selectedItem =
        this.selectedItem?.id === e.dataItem.id
          ? null
          : this.overview.items.find((x) => x.id === e.dataItem.id) ?? null;
      this.refreshAmountDefault();
      this.refreshHeadTypeDefault();
    },

    onProjectClick(e: any) {
      this.selectedProject = this.projects.find((x) => x.id === e.dataItem.id) ?? null;
    },

    // A sárga kérdés-sáv: megerősítéskor az onAccept, Mégse esetén az onReject fut. Egy
    // korábbi, megválaszolatlan kérdést az új felülír — annak elutasító ága lefut (pl. a
    // függőben lévő kilépés next(false)-ot kap)
    askQuestion(message: string, onAccept: () => void, onReject?: () => void) {
      const previousReject = this.pendingReject;
      this.questionMessage = message;
      this.pendingConfirm = onAccept;
      this.pendingReject = onReject ?? null;
      this.questionVisible = true;
      if (previousReject) previousReject();
    },

    acceptQuestion() {
      this.questionVisible = false;
      const action = this.pendingConfirm;
      this.pendingConfirm = null;
      this.pendingReject = null;
      if (action) action();
    },

    rejectQuestion() {
      this.questionVisible = false;
      const action = this.pendingReject;
      this.pendingConfirm = null;
      this.pendingReject = null;
      if (action) action();
    },

    // böngészőfül bezárása / frissítése: a mentetlen kapcsolat-módosításra a böngésző kérdez
    onBeforeUnload(e: BeforeUnloadEvent) {
      if (!this.linkDirty) return;
      e.preventDefault();
      e.returnValue = "";
    },

    // K2D pb_geni + ue_insert('head') kérdései, ablakonként egyszer: alkatrész-beszerzésnél
    // (type 9) a készletrendelés, mindig a nettó-kapcsolás. Szándékos eltérés: ha a számlán
    // már tétel-kapcsolat van, egyik kérdés sem jön (a K2D a készletrendelés-kérdést még a
    // kizárólagossági hiba előtt felteszi, a nettó-kérdést már nem) — a backend hibát ad
    assignHead() {
      if (!this.overview.links.some((l) => l.itemId > 0)) {
        if (this.overview.invType === 9 && !this.stockWarned) {
          this.askQuestion(
            t("PreAccounting_RaktaronAtfutoKeszletrendelesekProjektreTortenoK"),
            () => {
              this.stockWarned = true;
              this.assignHead();
            }
          );
          return;
        }
        if (!this.vatWarned) {
          this.askQuestion(
            t("PreAccounting_SzamlafejEsetenMindigNettoOsszegetKapcsolhat"),
            () => {
              this.vatWarned = true;
              this.assignHead();
            }
          );
          return;
        }
      }
      this.assign(0);
    },

    // K2D pb_geni (itemId=0) / pb_geni_item (itemId=tétel) + pb_ok — az ellenőrzések a
    // SetGeniProject-ben. A "nem a teljes összeg — folytatja?" kérdést a K2D az OK-nál teszi
    // fel; a TF a képernyő elhagyásakor (beforeRouteLeave), ezért itt megerősítve küldjük
    async assign(itemId: number) {
      if (!this.selectedProject) return;
      if (this.amountValue !== null && this.amountValue === 0) {
        eventBus.$emit("warn", t("PreAccounting_N0OsszegetNemAdhatMeg"));
        return;
      }
      // a felvitel utáni újratöltés a kijelölt kapcsolat mentetlen módosítását eldobná
      if (this.linkDirty) {
        this.askQuestion(t("PreAccounting_AKijeloltKapcsolatModositasaNincsElmentve_3"), () => {
          this.resetLinkEdit();
          this.assign(itemId);
        });
        return;
      }
      this.isSaving = true;
      try {
        const result = await PreAccountingApi.setGeniProject(
          this.invNo,
          this.selectedProject.id,
          itemId,
          0,
          this.amountValue,
          true,
          this.headTypeValue
        );
        eventBus.$emit(
          "success",
          itemId > 0
            ? t("PreAccounting_ATetelHozzarendelve", [result.projectName])
            : t("PreAccounting_ASzamlaHozzarendelve", [result.projectName])
        );
        this.sessionModified = true;
        await this.load();
        if (result.needsReaccount) this.reAccountConfirmVisible = true;
      } catch {
        // A backend üzenetét a globális hibakezelő már megjelenítette
      } finally {
        this.isSaving = false;
      }
    },

    // K2D pb_delete + pb_ok
    async deleteLink() {
      if (!this.selectedLink) return;
      const deletedIndex = this.overview.links.findIndex((x) => x.id === this.selectedLink!.id);
      this.isSaving = true;
      try {
        const result = await PreAccountingApi.setGeniProject(this.invNo, 1, 0, this.selectedLink.id);
        eventBus.$emit("success", t("PreAccounting_AProjektKapcsolatTorolve"));
        this.sessionModified = true;
        await this.load();
        // GL fókusz-konvenció: a helyére lépő kapcsolat (utolsónál az előző) lesz kijelölve,
        // kiürült listánál a felvitel típus-mezője kapja a fókuszt
        const links = this.overview.links;
        this.selectedLink = links.length > 0 ? links[Math.min(Math.max(deletedIndex, 0), links.length - 1)] : null;
        this.resetLinkEdit();
        this.$nextTick(() => {
          const target = this.selectedLink ? this.$refs.editAmount : this.$refs.newHeadType;
          (target as any)?.focus();
        });
        if (result.needsReaccount) this.reAccountConfirmVisible = true;
      } catch {
        // A backend üzenetét a globális hibakezelő már megjelenítette
      } finally {
        this.isSaving = false;
      }
    },

    // K2D dw_project_item szerkesztése + pb_ok: a kijelölt kapcsolat típusa, összege, megjegyzése
    // (a nem teljes kiosztás kérdése itt is a kilépéskor jön)
    async saveLink() {
      const link = this.selectedLink;
      if (!link) return;
      // type="number" mezőből törlés után üres string jön
      if (this.editAmount === null || String(this.editAmount) === "") {
        eventBus.$emit("warn", t("PreAccounting_AdjaMegAzOsszeget"));
        return;
      }
      if (this.editAmount === 0) {
        eventBus.$emit("warn", t("PreAccounting_N0OsszegetNemAdhatMeg"));
        return;
      }
      this.isSaving = true;
      try {
        const result = await PreAccountingApi.updateGeniProjectLink(
          this.invNo,
          link,
          this.editHeadType,
          this.editAmount,
          (this.editRemark ?? "").trim(),
          true
        );
        eventBus.$emit("success", t("PreAccounting_AProjektKapcsolatModositva"));
        this.sessionModified = true;
        await this.load();
        // a mentés után a gombok tiltottak lesznek — a fókusz az összeg-mezőre kerül
        this.$nextTick(() => (this.$refs.editAmount as any)?.focus());
        if (result.needsReaccount) this.reAccountConfirmVisible = true;
      } catch {
        // A backend üzenetét a globális hibakezelő már megjelenítette
      } finally {
        this.isSaving = false;
      }
    },

    // a kijelölt kapcsolat mentett értékei vissza a szerkesztő mezőkbe
    resetLinkEdit() {
      const link = this.selectedLink;
      this.editHeadType = link ? link.headType : 0;
      this.editAmount = link ? link.amount : null;
      this.editRemark = link ? link.remark ?? "" : "";
    },

    // K2D pb_ok lb_acc ága: f_invacc2_os → a TF auto-kontírja
    async reAccount() {
      this.isSaving = true;
      try {
        const result = await PreAccountingApi.reAccount("GENI", this.invNo, this.overview.accStatus);
        eventBus.$emit("success", t("PreAccounting_AutomatikusKontirozasMegtortentKonyvelesAllapot", [result.accStatusName]));
        if ((result.errors ?? []).length > 0) {
          eventBus.$emit("warn", t("PreAccounting_AKontirozasHibasNezzeMegA"));
        }
        await this.load();
      } catch {
        // A backend üzenetét a globális hibakezelő már megjelenítette
      } finally {
        this.isSaving = false;
      }
    },

    goBack() {
      this.$router.push(`/pre-accounting/GENI/${this.invNo}/edit`);
    },
  },
});
